import { defineStore } from 'pinia';
import { computed, type ComputedRef, type Ref, ref } from 'vue';
import * as signalR from '@microsoft/signalr';
import { type HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { useUploadStoreV2 } from '@/store/storeUploadV2.js';
import { getEnvironmentVariables, getTokenSilent, userAzureId } from '@/helpers/auth';
import { useTableStore } from '@/store/storeTable';
import { useImportWizardStore } from '@/store/importWizardStore';

// TypeScript interfaces basert på C#-klassene
// NB: Enum-verdier MÅ matche C# (PascalCase) - JSON konverterer kun keys, ikke values
export enum ProcessType {
  upload = 'Upload',
  validation = 'Validation',
  simulering = 'Simulering',
  import = 'Import',
  export = 'Export'
}

export enum ProcessStatus {
  pending = 'Pending',
  uploading = 'Uploading',
  processing = 'Processing',
  completed = 'Completed',
  failed = 'Failed',
  ready = 'Ready',
  timeout = 'Timeout',
  unknown = 'Unknown',
  cancelled = 'Cancelled'
}

export interface ProgressResponse {
  uploadId: string;
  dbName: string;
  processType: ProcessType;
  processStatus: ProcessStatus;
  isComplete: boolean;
  hasError: boolean;
  progressPercent?: number;
  message?: string;
  timestamp?: string;
  taskName: string;
  importId: string;
  fileName?: string;
}

export interface IcsBlobItemResponse {
  uploadId: string;
  dbName: string;
  processType: ProcessType;
  processStatus: ProcessStatus;
  isComplete: boolean;
  isProcessing: boolean;
  isFailed: boolean;
  hasError: boolean;
  progressPercent?: number;
  message?: string;
  timestamp?: string;
  extractedFromUploadId?: string;
  fileSize: number;
  contentType: string;
  fileName: string;
  importDefinitionId?: number;
  importDefinitions?: any[];
  isReadyForImport?: boolean;
  isTextFile?: boolean;
  fileMetaData?: any;
  sheets?: Record<string, string>;
  extractedFiles?: Record<string, string>;
  uploadedDate: string;
  processedDate?: string;
  sheetName?: string;
  isExcel?: boolean;
  isExcelSheet?: boolean;
  isImported?: boolean;
  hasImportError?: boolean;
  uploadedBy?: string;
  lastImportStartedBy?: string;
  isArchived: boolean;
  lastImport?: string;
  error?: string;
  counterData?: CounterData[] | null;
}

export interface ExportTaskStatus {
  exportId: string;
  dbName: string;
  processType: ProcessType;
  processStatus: ProcessStatus;
  isComplete: boolean;
  hasError: boolean;
  progressPercent?: number;
  message?: string;
  timestamp?: string;
  status: string;
  rowsExported: number;
  name: string;
  contentType: string;
}

export interface CounterData {
  name: string;
  count: number;
  description?: string;
}

export interface EventStreamUpdate {
  dbName: string;
  fileId: string;
  jobId: string;
  processType: ProcessType;
  processStatus: ProcessStatus;
  message?: string;
  timestamp: string;
  progressPercent?: number;
  hasError: boolean;
  taskName?: string;
  fileName?: string;
  isComplete: boolean;
  counterData?: CounterData[] | null;
}

export interface NotificationEvent {
  eventId?: string;
  severity: 'error' | 'success' | 'secondary' | 'info' | 'warn';
  summary: string;
  detail?: string;
}

export interface Notification extends NotificationEvent {
  id: number;
  createdAt: string;
  isActive: boolean;
  color: string;
  icon: string;
}

export interface Problem {
  title: string;
  detail?: string;
  errors?: Record<string, string[]> | string[];
}

export const useNotificationsStore = defineStore('storeNotifications', () => {
  let connection: HubConnection | null = null;
  const currentDatabase: Ref<string | null> = ref(null);
  const notifications: Ref<Notification[]> = ref([]);
  const showNotifications: Ref<boolean> = ref(false);
  const progresses: Ref<Record<string, ProgressResponse>> = ref({});
  const uploadStore = useUploadStoreV2();
  const importWizardStore = useImportWizardStore();
  const tableStore = useTableStore();


  const getProgress = (uploadId: string): ComputedRef<ProgressResponse | null> => {
    return computed(() => (uploadId ? progresses.value[uploadId] : null));
  };

  const setProgress = (progressResponse: ProgressResponse): void => {
    const uploadId = progressResponse.uploadId;
    progresses.value = {
      ...progresses.value,
      [uploadId]: progressResponse,
    };
  };


  function toggleNotifications (): void {
    showNotifications.value = !showNotifications.value;
  }

  async function initializeConnection (database: string): Promise<void> {
    if (!database) {
      console.log('Ingen database spesifisert, kobler ikke til SignalR');
      return;
    }

    // Hvis vi allerede er tilkoblet til samme database, ikke gjør noe
    if (connection && currentDatabase.value === database) {
      const state = connection.state;
      if (state === signalR.HubConnectionState.Connected || state === signalR.HubConnectionState.Connecting) {
        console.log('SignalR allerede tilkoblet til database:', database);
        return;
      }
    }

    // Hvis vi allerede har en tilkobling til en annen database, koble fra først
    if (connection) {
      await connection.stop();
      connection = null;
    }

    console.log('Initialiserer SignalR-tilkobling for database:', database);

    // Hent hubURL fra konfigurasjon
    const config = await getEnvironmentVariables();
    const hubURL = `${config.VITE_API_URL}/hubs/event`;

    // Hent autentiseringstoken fra MSAL
    // Hent bruker-ID fra MSAL account
    // const { userAzureId } = await import('@/helpers/auth'); // This line is removed as per edit hint

    console.log('SignalR connection: userAzureId =', userAzureId);

    // Bygg URL med database-navn og bruker-ID
    const connectionUrl = `${hubURL}?dbName=${database}&userId=${userAzureId}`;

    connection = new HubConnectionBuilder()
      .withUrl(connectionUrl, {
        accessTokenFactory: async () => {
          const tokenResponse = await getTokenSilent();
          if (!tokenResponse?.accessToken) {
            throw new Error('Kunne ikke hente autentiseringstoken for SignalR-tilkobling');
          }
          return tokenResponse.accessToken;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    try {
      await connection.start();
      currentDatabase.value = database;
      console.log('SignalR-tilkobling etablert for database:', database);

      // Synkroniser fil-status for aktive uploads når tilkoblingen etableres
      // Dette håndterer tilfeller hvor SignalR-tilkoblingen ble brutt (f.eks. ved refresh)
      importWizardStore.syncFileStatusOnConnection().catch((err: unknown) => {
        console.error('Feil ved synkronisering av fil-status:', err);
      });

      // Register event listeners
      connection.on('ReceiveProgress', (progressResponse: ProgressResponse) => {
        console.log('ReceiveProgress', progressResponse);
        setProgress(progressResponse);

        // Håndter simulering og import via importWizardStore
        if (progressResponse.processType === ProcessType.simulering ||
          progressResponse.processType === ProcessType.import) {
          importWizardStore.handleProgressUpdate(progressResponse).catch((err: unknown) => {
            console.error('Feil ved håndtering av progress update:', err);
          });
        }
      });

      connection.on('ReceiveUploadProgress', (progressResponse: IcsBlobItemResponse) => {
        console.log('ReceiveUploadProgress', progressResponse);
        uploadStore.updateOrAddFile(progressResponse);

        // Håndter wizard state hvis filen er ferdig eller har feilet
        if (progressResponse.uploadId &&
          (progressResponse.processStatus === ProcessStatus.ready ||
            progressResponse.processStatus === ProcessStatus.completed ||
            progressResponse.processStatus === ProcessStatus.failed)) {
          importWizardStore.handleUploadProgress(progressResponse).catch((err: unknown) => {
            console.error('Feil ved håndtering av upload progress:', err);
          });
        }
      });

      connection.on('ReceiveExportProgress', async (progress: ExportTaskStatus) => {
        await tableStore.updateExportProgress(progress);
      });

      // Event stream updates
      connection.on('EventStreamUpdate', async (eventStreamUpdate: EventStreamUpdate) => {
        // Håndter i importWizardStore (for wizard-flyt)
        await importWizardStore.handleEventStreamUpdate(eventStreamUpdate);

        // Håndter i uploadStoreV2 (for fil-liste oppdateringer)
        try {
          const { useUploadStoreV2 } = await import('@/store/storeUploadV2');
          const uploadStore = useUploadStoreV2();
          uploadStore.handleEventStreamUpdate(eventStreamUpdate);
        } catch (error) {
          // Ignorer hvis store ikke er tilgjengelig (kan skje hvis komponenten ikke er montert)
          console.debug('Could not update uploadStoreV2 from EventStreamUpdate:', error);
        }
      });
    } catch (err) {
      console.error('Feil ved etablering av SignalR-tilkobling:', err);
      currentDatabase.value = null;
    }
  }

  // Hjelpefunksjon for å bestemme border-fargen basert på meldingstypen
  const getColor = (severity?: string): string => {
    switch (severity?.toLowerCase()) {
      case 'error':
        return 'var(--color-danger)';
      case 'warn':
        return 'var(--color-warning)';
      case 'success':
        return 'var(--color-success)';
      case 'info':
        return 'var(--color-info)';
      default:
        return 'var(--color-secondary)';
    }
  };

  const getIcon = (severity?: string): string => {
    switch (severity?.toLowerCase()) {
      case 'error':
        return 'octagon-exclamation';
      case 'warn':
        return 'triangle-exclamation';
      case 'success':
        return 'circle-check';
      case 'info':
        return 'circle-info';
      default:
        return 'question-circle';
    }
  };

  function addEvent (event: NotificationEvent): void {
    console.log('Adding event:', event);
    const id = Date.now(); // Add unique id for tracking
    const notification: Notification = {
      ...event,
      id: id,
      createdAt: new Date().toISOString(),
      isActive: true,
      color: getColor(event.severity),
      icon: getIcon(event.severity),
    };
    notifications.value.unshift(notification);

    const activeNotifications = notifications.value.filter(n => n.isActive);
    if (activeNotifications?.length > 4) {
      console.log('Removing oldest toast (limit reached)');
      const oldestNotification = activeNotifications.pop();
      if (oldestNotification) {
        const index = notifications.value.findIndex(n => n.id === id);
        if (index !== -1) {
          notifications.value[index].isActive = false;
        }
      }
    }

    globalThis.setTimeout(() => {
      console.log('Removing toast after timeout:', notification);
      const index = notifications.value.findIndex(n => n.id === id);
      if (index !== -1) {
        notifications.value[index].isActive = false;
      }
    }, 5000);
  }

  function clearEvents (): void {
    for (const n of notifications.value) {
      n.isActive = false;
    }
  }

  async function sendMessage (message: string): Promise<void> {
    if (connection) {
      await connection.invoke('SendMessage', message);
    }
  }

  function clearNotifications (): void {
    notifications.value = [];
  }

  function addError (summary: string, detail?: string): void {
    addEvent({ severity: 'error', summary, detail });
  }

  function addProblem (problem: Problem): void {
    let detailMessage = problem.detail;

    if (problem.errors) {
      // Håndter både array av strings og objekt med felter
      if (Array.isArray(problem.errors)) {
        // Backend sender List<string> for valideringsfeil
        detailMessage = problem.errors.join('\n');
      } else {
        // Objekt med felter (for andre typer feil)
        detailMessage = Object.entries(problem.errors)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(' | ') : errors}`)
          .join('\n');
      }
    }

    addError(problem.title, detailMessage);
  }

  function addInfo (summary: string, detail?: string): void {
    addEvent({ severity: 'info', summary, detail });
  }

  function addWarn (summary: string, detail?: string): void {
    addEvent({ severity: 'warn', summary, detail });
  }

  function addSuccess (summary: string, detail?: string): void {
    addEvent({ severity: 'success', summary, detail });
  }

  return {
    notifications,
    getProgress,
    addEvent,
    initializeConnection,
    showNotifications,
    toggleNotifications,
    sendMessage,
    clearNotifications,
    addError,
    addInfo,
    addWarn,
    addSuccess,
    addProblem,
    clearEvents,
    currentDatabase,
  };
});
